/**
 * Server-sent recipe programs (R1a, D2). The server sends each run's recipe as a program (only
 * the fields the runner reads) with its hash, to a runner that advertises recipe_ops:2. The
 * runner checks the hash, lints the program (os-recipe-lint.ts) and only then walks it, so a new
 * recipe ships with a server deploy and never waits for runners to converge on a new bundle. A
 * runner without the feature gets only the legacy recipes, by id. Pure and browser-safe.
 */
import type { OsRecipe, OsRecipeParam, OsRecipeStep, OsRecipeTier, OsRecipeVerify } from './os-recipes.js';

export interface OsRecipeProgram {
  id: string;
  app: { bundleId: string; name: string } | null;
  system?: true;
  params: Readonly<Record<string, OsRecipeParam>>;
  steps: readonly OsRecipeStep[];
  onYes?: readonly OsRecipeStep[];
  tier: OsRecipeTier;
  /** The runner reads it: a contact recipe is verified by construction once its app is in front. */
  verify?: OsRecipeVerify;
  sendKey?: string;
}

/** The ids a runner without recipe_ops:2 has in its own table, frozen: nothing else is ever dialed
 *  to it. */
export const LEGACY_RECIPE_IDS: ReadonlySet<string> = new Set([
  'slack.open_conversation', 'slack.draft_message', 'slack.unreads', 'slack.dms', 'slack.back', 'chrome.new_tab',
  'chrome.go_to', 'chrome.search', 'chrome.switch_tab', 'messages.open_conversation', 'messages.draft',
  'whatsapp.open_chat', 'whatsapp.draft', 'mail.new_message', 'mail.draft_to', 'calendar.today', 'notes.new_note',
  'app.send_draft',
]);

/** Only the fields the runner reads. */
export function toProgram(r: OsRecipe): OsRecipeProgram {
  return {
    id: r.id,
    app: r.app,
    ...(r.system === true ? { system: true as const } : {}),
    params: r.params,
    steps: r.steps,
    ...(r.onYes ? { onYes: r.onYes } : {}),
    tier: r.tier,
    ...(r.verify ? { verify: r.verify } : {}),
    ...(r.sendKey !== undefined ? { sendKey: r.sendKey } : {}),
  };
}

/** JSON with keys sorted at every level and no whitespace: one spelling per value. */
export function canonicalJson(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map((x) => canonicalJson(x === undefined ? null : x)).join(',')}]`;
  if (v !== null && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o).filter((k) => o[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${canonicalJson(o[k])}`).join(',')}}`;
  }
  return JSON.stringify(v) ?? 'null';
}

/** 'f1:' + FNV-1a 64 over the program's canonical JSON (UTF-8), as 16 hex characters. */
export function programHash(p: OsRecipeProgram): string {
  let h = 0xcbf29ce484222325n;
  for (const c of new TextEncoder().encode(canonicalJson(p))) {
    h ^= BigInt(c);
    h = (h * 0x100000001b3n) & 0xffffffffffffffffn;
  }
  return `f1:${h.toString(16).padStart(16, '0')}`;
}

/** The recipe the runner walks. Title, description, reply and cues are the server's: the runner
 *  never reads them. */
export function recipeFromProgram(p: OsRecipeProgram): OsRecipe {
  return {
    id: p.id,
    app: p.app,
    ...(p.system === true ? { system: true as const } : {}),
    title: '',
    description: '',
    params: p.params,
    steps: p.steps,
    ...(p.onYes ? { onYes: p.onYes } : {}),
    tier: p.tier,
    ...(p.verify ? { verify: p.verify } : {}),
    cues: [],
    ...(p.sendKey !== undefined ? { sendKey: p.sendKey } : {}),
    reply: '',
  };
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isStepList = (v: unknown): boolean => Array.isArray(v) && v.every((s) => isRecipeStep(s));
const PARAM_KINDS: ReadonlySet<string> = new Set(['span', 'literal', 'enum', 'number', 'url']);
/** A param of a known kind; an enum's values are text. */
const isParam = (p: unknown): boolean =>
  isObject(p) && typeof p.kind === 'string' && PARAM_KINDS.has(p.kind) &&
  (p.enum === undefined || (Array.isArray(p.enum) && p.enum.every((e) => typeof e === 'string')));

const isStr = (v: unknown): v is string => typeof v === 'string';
const isNum = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v);
const isStrList = (v: unknown): boolean => Array.isArray(v) && v.every(isStr);
/** Each op's own fields, as the runner and the lint read them. An op this build does not know
 *  passes here: the lint names it. */
const STEP_FIELDS: Readonly<Record<string, (s: Record<string, unknown>) => boolean>> = {
  key: (s) => isStr(s.chord),
  send_key: (s) => isStr(s.chord),
  type: (s) => isStr(s.param),
  settle: (s) => isNum(s.ms),
  focus_moved: (s) => isNum(s.timeoutMs) && isStrList(s.labelIn),
  focus_is: (s) => isNum(s.timeoutMs) && isStrList(s.labelIn),
  verify_title: (s) => isStr(s.param) && isNum(s.timeoutMs),
  verify_title_has: (s) => isStr(s.param) && isNum(s.timeoutMs),
  contact: (s) => isStr(s.param) && (s.want === 'handle' || s.want === 'phone' || s.want === 'email'),
  open_url: (s) => isStr(s.template) && (s.bundleId === undefined || isStr(s.bundleId)),
  await_front: (s) => isNum(s.timeoutMs),
  verb: (s) => isStr(s.id) && isObject(s.params) && Object.values(s.params).every((v) => isStr(v) || typeof v === 'boolean' || isNum(v)),
  pick_tab: (s) => isStr(s.param),
  cycle_to_title: (s) => isStr(s.chord) && isStr(s.param) && isNum(s.max),
  menu: (s) => isStrList(s.path) && (s.pair === undefined || (isStrList(s.pair) && (s.pair as unknown[]).length === 2)),
  resolve: (s) => isStr(s.source) && isStr(s.param) && isStr(s.into),
};
/** A step with its op's own fields in shape (an op this build does not know passes: the lint names it). */
export const isRecipeStep = (s: unknown): boolean =>
  isObject(s) && isStr(s.op) && (!Object.prototype.hasOwnProperty.call(STEP_FIELDS, s.op) || STEP_FIELDS[s.op]!(s));

/** A frame shaped like a program, before the hash and the lint read it. */
export function isRecipeProgram(v: unknown): v is OsRecipeProgram {
  if (!isObject(v) || typeof v.id !== 'string' || typeof v.tier !== 'string' || !isObject(v.params) || !isStepList(v.steps)) return false;
  if (!Object.values(v.params).every(isParam)) return false;
  if (v.onYes !== undefined && !isStepList(v.onYes)) return false;
  if (v.sendKey !== undefined && typeof v.sendKey !== 'string') return false;
  return v.app === null || (isObject(v.app) && typeof v.app.bundleId === 'string' && typeof v.app.name === 'string');
}
